<script setup>
import {
  computed,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { internalCall } from 'dashboard/lib/internalCallSession';
import { reconcileCallSelection } from 'dashboard/lib/internalCallLayout';
import { removeCallParticipant } from 'dashboard/api/internalCalls';
import Dialog from '../dialog/Dialog.vue';
import CallControl from './CallControl.vue';
import CallMediaTile from './CallMediaTile.vue';
import CallSettings from './CallSettings.vue';
import CallChat from './CallChat.vue';

const { t } = useI18n();
const { accountId } = useAccount();
const state = internalCall.state;
const shell = ref(null);
const recoveryControl = ref(null);
const media = ref(null);
const returnDialog = ref(null);
const expanded = ref(false);
const mode = ref('focus');
const selectedId = ref(null);
const newStreams = ref([]);
const thumbnailStrip = ref(null);
const camerasOpen = ref(true);
const participants = ref([]);
const sidePanel = ref('');
const chatDraft = ref('');
const busy = ref(false);
const pipTarget = ref(null);
const pipSupported = 'documentPictureInPicture' in window;
const chatOpen = ref(
  localStorage.getItem('internal-call-chat-open') !== 'false'
);
let pipWindow;
let themeObserver;
let previousIds = [];
let lastControl;
const tracks = computed(() => state.tracks || []);
const nextNewStream = computed(() =>
  tracks.value.find(item => newStreams.value.includes(item.id))
);
const selected = computed(() =>
  tracks.value.find(item => item.id === selectedId.value)
);
const screens = computed(() =>
  tracks.value.filter(item => item.source === 'screen_share')
);
const cameras = computed(() =>
  tracks.value.filter(item => item.source !== 'screen_share')
);
const mainTracks = computed(() => {
  if (!expanded.value || mode.value === 'focus')
    return selected.value ? [selected.value] : [];
  return selected.value?.source === 'camera' || !screens.value.length
    ? cameras.value
    : screens.value;
});
const thumbnails = computed(() =>
  mode.value === 'grid'
    ? tracks.value.filter(item => !mainTracks.value.includes(item))
    : tracks.value
);
const activePanel = computed(() =>
  expanded.value
    ? sidePanel.value || (chatOpen.value ? 'chat' : '')
    : sidePanel.value
);
const canAct = computed(
  () => !busy.value && !state.paused && state.phase === 'connected'
);
const canRecover = computed(
  () =>
    String(accountId.value) === String(state.accountId) &&
    state.roomId &&
    (state.transferRequired || state.error === 'connection_failed')
);
const phaseLabel = computed(() => {
  let label = state.paused ? 'PAUSED' : state.phase.toUpperCase();
  if (state.phase === 'idle' && state.error) label = 'NOT_CONNECTED';
  if (state.transferRequired && state.phase === 'idle') label = 'OTHER_TAB';
  if (state.transferring) label = 'TRANSFERRING';
  return t(`INTERNAL_CALL.${label}`);
});
const emptyLabel = computed(() => {
  if (state.phase === 'connected' && !state.paused) return 'WAITING';
  if (state.transferRequired && !state.transferring) return 'TRANSFER_HELP';
  return 'NOT_CONNECTED_HELP';
});
const recover = () => {
  if (!canRecover.value || state.phase !== 'idle') return;
  internalCall.start(
    state.accountId,
    { id: state.roomId, name: state.roomName },
    Boolean(state.transferRequired)
  );
};
const run = async action => {
  busy.value = true;
  try {
    await internalCall.run(action);
  } finally {
    busy.value = false;
  }
};
const choose = id => {
  selectedId.value = id;
  newStreams.value = newStreams.value.filter(item => item !== id);
  mode.value = 'focus';
};
const viewNewStream = async () => {
  choose(nextNewStream.value.id);
  await nextTick();
  thumbnailStrip.value?.querySelector('[aria-pressed="true"]')?.focus();
};
watch(
  () => tracks.value.map(item => item.id),
  ids => {
    const result = reconcileCallSelection(
      tracks.value,
      selectedId.value,
      previousIds,
      newStreams.value
    );
    selectedId.value = result.selectedId;
    newStreams.value = result.unseen;
    previousIds = [...ids];
  },
  { immediate: true }
);
watch(chatOpen, value =>
  localStorage.setItem('internal-call-chat-open', String(value))
);
watch(
  () => [accountId.value, state.accountId, state.roomId],
  () => {
    chatDraft.value = '';
  }
);
watch(media, el => internalCall.setContainer(el));
watch(accountId, value => {
  if (state.accountId && String(value) !== String(state.accountId))
    run(() => internalCall.close());
});
const closePip = () => {
  pipWindow?.close();
  pipTarget.value = null;
  themeObserver?.disconnect();
  pipWindow = null;
};
watch(
  () => state.phase,
  async (phase, previousPhase) => {
    if (phase === 'idle') {
      const focused = shell.value?.ownerDocument.activeElement;
      const restoreRecovery =
        previousPhase === 'connecting' &&
        canRecover.value &&
        shell.value?.contains(focused);
      expanded.value = false;
      sidePanel.value = '';
      participants.value = [];
      selectedId.value = null;
      newStreams.value = [];
      previousIds = [];
      closePip();
      await nextTick();
      if (
        restoreRecovery &&
        !focused.isConnected &&
        focused.ownerDocument.activeElement === focused.ownerDocument.body
      ) {
        recoveryControl.value?.focus();
      }
    }
  }
);
watch(
  () => state.returnOffer,
  offered => {
    if (offered) returnDialog.value?.open();
    else returnDialog.value?.close();
  }
);
const closePanel = async () => {
  if (sidePanel.value) sidePanel.value = '';
  else chatOpen.value = false;
  await nextTick();
  // The restored chat may focus its heading on mount. Restore the visible
  // settings/participants trigger after that mount has completed.
  await nextTick();
  lastControl?.focus();
};
const togglePanel = (name, event) => {
  lastControl = event.currentTarget;
  if (name === 'chat') {
    if (!expanded.value) expanded.value = true;
    if (sidePanel.value) {
      sidePanel.value = '';
      chatOpen.value = true;
    } else chatOpen.value = !chatOpen.value;
  } else sidePanel.value = sidePanel.value === name ? '' : name;
};
const toggleExpanded = () => {
  if (pipWindow) {
    closePip();
    expanded.value = true;
  } else expanded.value = !expanded.value;
  sidePanel.value = '';
};
const openPip = async () => {
  if (pipWindow) {
    closePip();
    return;
  }
  await internalCall.run(async () => {
    const nextWindow = await window.documentPictureInPicture.requestWindow({
      width: 420,
      height: 360,
    });
    if (state.phase === 'idle') {
      nextWindow.close();
      return;
    }
    pipWindow = nextWindow;
    document
      .querySelectorAll('link[rel="stylesheet"], style')
      .forEach(node =>
        pipWindow.document.head.appendChild(node.cloneNode(true))
      );
    const syncTheme = () => {
      if (pipWindow) {
        pipWindow.document.documentElement.className =
          document.documentElement.className;
        pipWindow.document.body.className = document.body.className;
      }
    };
    syncTheme();
    themeObserver = new MutationObserver(syncTheme);
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    themeObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
    expanded.value = false;
    sidePanel.value = '';
    pipTarget.value = pipWindow.document.body;
    pipWindow.addEventListener('pagehide', () => {
      pipTarget.value = null;
      pipWindow = null;
      themeObserver?.disconnect();
    });
  }, 'action_failed');
};
const showParticipants = async event => {
  togglePanel('participants', event);
  if (sidePanel.value !== 'participants') return;
  await internalCall.run(async () => {
    participants.value = (await internalCall.status()).participants || [];
  }, 'action_failed');
};
const remove = async userId => {
  await removeCallParticipant(internalCall.getUrl(), userId);
  participants.value = (await internalCall.status()).participants || [];
};
const keydown = event => {
  if (event.key === 'Escape') {
    if (activePanel.value) closePanel();
    else if (expanded.value) expanded.value = false;
    return;
  }
  if (event.key !== 'Tab' || !expanded.value) return;
  const focusable = [
    ...shell.value.querySelectorAll(
      'button:not(:disabled), select:not(:disabled), textarea:not(:disabled), input:not(:disabled), [tabindex="0"]'
    ),
  ].filter(el => el.getClientRects().length);
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && event.target === first) {
    event.preventDefault();
    last?.focus();
  } else if (!event.shiftKey && event.target === last) {
    event.preventDefault();
    first?.focus();
  }
};
const deviceChanged = () =>
  internalCall.run(() => internalCall.recoverPlayback());
onMounted(() =>
  navigator.mediaDevices?.addEventListener('devicechange', deviceChanged)
);
onBeforeUnmount(() => {
  closePip();
  navigator.mediaDevices?.removeEventListener('devicechange', deviceChanged);
  internalCall.run(() => internalCall.close());
});
</script>

<template>
  <Teleport :to="pipTarget || 'body'" :disabled="!pipTarget">
    <section
      v-show="state.phase !== 'idle' || state.error"
      ref="shell"
      :role="expanded ? 'dialog' : 'region'"
      :aria-modal="expanded || undefined"
      :aria-label="t('INTERNAL_CALL.TITLE')"
      class="fixed z-50 flex flex-col overflow-hidden border border-n-weak bg-n-background text-n-slate-12 shadow-2xl"
      :class="
        pipTarget
          ? 'inset-0'
          : expanded
            ? 'inset-0 rounded-none sm:inset-4 sm:rounded-2xl'
            : 'bottom-4 right-4 max-h-[calc(100dvh-2rem)] w-[380px] max-w-[calc(100vw-2rem)] rounded-2xl'
      "
      @keydown="keydown"
    >
      <header
        class="flex shrink-0 items-center justify-between gap-2 border-b border-n-weak px-3 py-2.5 sm:px-4"
      >
        <div class="flex min-w-0 items-center gap-2.5">
          <span
            class="flex size-9 shrink-0 items-center justify-center rounded-xl bg-n-blue-3 text-n-blue-11"
          >
            <span class="i-lucide-audio-lines size-5" aria-hidden="true" />
          </span>
          <div class="min-w-0">
            <h2 class="m-0 truncate text-sm font-semibold">
              {{ state.roomName || t('INTERNAL_CALL.TITLE') }}
            </h2>
            <p
              role="status"
              class="m-0 mt-0.5 flex items-center gap-1.5 text-xs text-n-slate-11"
            >
              <span
                class="size-1.5 rounded-full"
                :class="
                  state.phase === 'connected' && !state.paused
                    ? 'bg-n-teal-9'
                    : 'bg-n-amber-9'
                "
                aria-hidden="true"
              />{{ phaseLabel }}
            </p>
          </div>
        </div>
        <div class="flex shrink-0 gap-1">
          <CallControl
            v-if="expanded"
            :icon="
              mode === 'focus' ? 'i-lucide-grid-2x2' : 'i-lucide-panel-top'
            "
            :label="
              t(
                mode === 'focus'
                  ? 'INTERNAL_CALL.GRID'
                  : 'INTERNAL_CALL.SPOTLIGHT'
              )
            "
            :active="mode === 'grid'"
            :aria-pressed="mode === 'grid'"
            @click="mode = mode === 'focus' ? 'grid' : 'focus'"
          />
          <CallControl
            v-if="pipSupported"
            icon="i-lucide-picture-in-picture-2"
            :label="
              t(
                pipTarget ? 'INTERNAL_CALL.CLOSE_PIP' : 'INTERNAL_CALL.OPEN_PIP'
              )
            "
            :active="Boolean(pipTarget)"
            @click="openPip"
          />
          <CallControl
            v-if="state.phase !== 'idle' || canRecover"
            :icon="expanded ? 'i-lucide-minus' : 'i-lucide-expand'"
            :label="
              t(expanded ? 'INTERNAL_CALL.MINIMIZE' : 'INTERNAL_CALL.EXPAND')
            "
            :aria-expanded="expanded"
            @click="toggleExpanded"
          />
        </div>
      </header>
      <div
        v-if="state.error"
        role="alert"
        class="flex shrink-0 items-center justify-between gap-2 border-b border-n-ruby-6 bg-n-ruby-2 px-4 py-2 text-sm text-n-ruby-11"
      >
        <span>{{ t(`INTERNAL_CALL.ERROR.${state.error}`) }}</span
        ><CallControl
          icon="i-lucide-x"
          :label="t('INTERNAL_CALL.DISMISS_ERROR')"
          @click="internalCall.dismissError()"
        />
      </div>
      <div
        v-if="canRecover || state.transferring"
        class="shrink-0 border-b border-n-weak px-4 py-3"
      >
        <button
          ref="recoveryControl"
          type="button"
          :aria-disabled="state.phase !== 'idle'"
          :aria-busy="state.transferring"
          class="min-h-11 w-full rounded-lg bg-n-blue-12 px-3 py-2 text-sm font-semibold text-n-blue-1 hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand aria-disabled:cursor-wait aria-disabled:opacity-70"
          @click="recover"
        >
          {{
            t(
              state.transferring
                ? 'INTERNAL_CALL.TRANSFERRING'
                : state.transferRequired
                  ? 'INTERNAL_CALL.TRANSFER'
                  : 'INTERNAL_CALL.RETRY'
            )
          }}
        </button>
      </div>
      <div
        v-if="state.playbackBlocked"
        class="flex shrink-0 flex-wrap items-center justify-between gap-2 bg-n-amber-2 px-4 py-2 text-xs text-n-amber-11"
        role="status"
      >
        <span>{{ t('INTERNAL_CALL.PLAYBACK_BLOCKED') }}</span
        ><button
          type="button"
          class="font-semibold underline"
          @click="run(() => internalCall.recoverPlayback())"
        >
          {{ t('INTERNAL_CALL.PLAYBACK') }}
        </button>
      </div>
      <div
        class="relative flex min-h-0 flex-1"
        :class="{ 'min-h-44': !expanded }"
      >
        <main
          class="min-h-0 min-w-0 flex-1 flex-col"
          :class="[
            expanded ? 'gap-3 p-3 sm:p-4' : 'gap-2 p-3',
            activePanel ? (expanded ? 'hidden md:flex' : 'hidden') : 'flex',
          ]"
        >
          <div
            v-if="mainTracks.length"
            class="min-h-0 flex-1 gap-3"
            :class="
              mode === 'grid' && expanded
                ? 'grid auto-rows-fr grid-cols-1 overflow-y-auto sm:grid-cols-2'
                : 'flex'
            "
          >
            <CallMediaTile
              v-for="item in mainTracks"
              :key="item.id"
              :item="item"
              class="min-h-40 w-full flex-1"
              :class="{ 'aspect-video': !expanded }"
            />
          </div>
          <div
            v-else
            class="flex min-h-40 flex-1 flex-wrap items-center justify-center gap-4 rounded-xl border border-n-weak bg-n-alpha-1 p-6"
          >
            <div
              v-for="participant in state.participants"
              :key="participant.identity"
              class="flex max-w-32 flex-col items-center gap-2"
            >
              <span
                class="flex size-16 items-center justify-center rounded-full bg-n-blue-3 text-xl font-semibold text-n-blue-11"
                >{{ participant.name?.slice(0, 1) }}</span
              ><span class="max-w-full truncate text-xs font-medium">{{
                participant.name
              }}</span>
            </div>
            <div v-if="!state.participants.length" class="text-center">
              <span
                class="i-lucide-headphones mx-auto mb-3 block size-8 text-n-slate-10"
                aria-hidden="true"
              />
              <p class="m-0 text-sm text-n-slate-11">
                {{ t(`INTERNAL_CALL.${emptyLabel}`) }}
              </p>
            </div>
          </div>
          <div v-if="expanded && thumbnails.length" class="shrink-0">
            <button
              v-if="nextNewStream"
              type="button"
              class="mb-2 flex min-h-10 w-full items-center justify-center gap-2 rounded-xl bg-n-blue-3 px-3 py-2 text-xs font-semibold text-n-blue-12 ring-1 ring-inset ring-n-blue-6 hover:bg-n-blue-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
              @click="viewNewStream"
            >
              <span
                class="i-lucide-monitor-up size-4 shrink-0"
                aria-hidden="true"
              />
              <span class="min-w-0 break-words">{{
                t('INTERNAL_CALL.VIEW_NEW_STREAM', { name: nextNewStream.name })
              }}</span>
            </button>
            <button
              v-if="mode === 'grid'"
              type="button"
              class="mb-2 flex min-h-8 items-center gap-2 text-xs font-medium text-n-slate-11 focus-visible:outline-n-brand"
              :aria-expanded="camerasOpen"
              @click="camerasOpen = !camerasOpen"
            >
              <span
                class="size-4"
                :class="
                  camerasOpen
                    ? 'i-lucide-chevron-down'
                    : 'i-lucide-chevron-right'
                "
                aria-hidden="true"
              />{{
                t(
                  selected?.source === 'camera'
                    ? 'INTERNAL_CALL.SCREENS'
                    : 'INTERNAL_CALL.CAMERAS'
                )
              }}
              · {{ thumbnails.length }}
            </button>
            <div
              v-if="mode !== 'grid' || camerasOpen"
              ref="thumbnailStrip"
              class="flex gap-2 overflow-x-auto p-1"
              :class="{ 'sm:justify-center': thumbnails.length < 5 }"
            >
              <CallMediaTile
                v-for="item in thumbnails"
                :key="item.id"
                :item="item"
                :selected="selectedId === item.id"
                thumbnail
                :is-new="newStreams.includes(item.id)"
                class="aspect-video h-20 shrink-0 sm:h-24"
                @select="choose"
              />
            </div>
          </div>
          <p v-if="newStreams.length" class="sr-only" role="status">
            {{ t('INTERNAL_CALL.NEW_STREAM_AVAILABLE') }}
          </p>
        </main>
        <aside
          v-if="activePanel"
          class="z-20 flex min-h-0 flex-col border-l border-n-weak bg-n-background"
          :class="
            expanded
              ? 'absolute inset-0 md:static md:w-80 md:shrink-0'
              : 'absolute inset-0'
          "
        >
          <CallSettings v-if="activePanel === 'settings'" @close="closePanel" />
          <CallChat
            v-else-if="activePanel === 'chat'"
            v-model:draft="chatDraft"
            :room-id="state.roomId"
            @close="closePanel"
          />
          <section
            v-else
            class="flex min-h-0 flex-1 flex-col p-4"
            :aria-label="t('INTERNAL_CALL.PARTICIPANTS')"
          >
            <header class="mb-4 flex items-center justify-between">
              <h3 class="m-0 text-base font-semibold">
                {{ t('INTERNAL_CALL.PARTICIPANTS') }}
              </h3>
              <CallControl
                icon="i-lucide-x"
                :label="t('INTERNAL_CALL.CLOSE_PANEL')"
                @click="closePanel"
              />
            </header>
            <ul class="m-0 space-y-3 overflow-y-auto p-0">
              <li
                v-for="participant in participants"
                :key="participant.user_id"
                class="flex items-center justify-between gap-2 text-sm"
              >
                <span class="truncate">{{ participant.name }}</span
                ><button
                  v-if="state.canRemove"
                  type="button"
                  class="rounded-lg px-2 py-1 text-xs text-n-ruby-11 hover:bg-n-ruby-3"
                  @click="run(() => remove(participant.user_id))"
                >
                  {{ t('INTERNAL_CALL.REMOVE') }}
                </button>
              </li>
            </ul>
          </section>
        </aside>
      </div>
      <footer
        v-if="state.phase !== 'idle'"
        class="flex shrink-0 flex-wrap items-center justify-center gap-2 border-t border-n-weak bg-n-background px-2 py-3 sm:gap-3"
      >
        <CallControl
          :icon="state.muted ? 'i-lucide-mic-off' : 'i-lucide-mic'"
          :label="
            t(state.muted ? 'INTERNAL_CALL.UNMUTE' : 'INTERNAL_CALL.MUTE')
          "
          :active="!state.muted"
          :aria-pressed="!state.muted"
          :disabled="
            state.paused || state.phase !== 'connected' || (busy && state.muted)
          "
          @click="run(() => internalCall.setMuted(!state.muted))"
        />
        <CallControl
          :icon="
            state.settings.deafened
              ? 'i-lucide-headphone-off'
              : 'i-lucide-headphones'
          "
          :label="
            t(
              state.settings.deafened
                ? 'INTERNAL_CALL.UNDEAFEN'
                : 'INTERNAL_CALL.DEAFEN'
            )
          "
          :active="state.settings.deafened"
          :aria-pressed="state.settings.deafened"
          :disabled="!canAct"
          @click="run(() => internalCall.toggleDeafened())"
        />
        <CallControl
          :icon="state.camera ? 'i-lucide-video' : 'i-lucide-video-off'"
          :label="t('INTERNAL_CALL.CAMERA')"
          :active="state.camera"
          :aria-pressed="state.camera"
          :disabled="!canAct"
          @click="run(() => internalCall.toggleVisual('camera'))"
        />
        <CallControl
          :icon="
            state.screen ? 'i-lucide-screen-share-off' : 'i-lucide-screen-share'
          "
          :label="
            t(
              state.screen
                ? 'INTERNAL_CALL.STOP_SCREEN'
                : 'INTERNAL_CALL.SCREEN'
            )
          "
          :active="state.screen"
          :aria-pressed="state.screen"
          :disabled="!canAct"
          @click="run(() => internalCall.toggleVisual('screen'))"
        />
        <CallControl
          v-if="expanded"
          icon="i-lucide-users"
          :label="t('INTERNAL_CALL.PARTICIPANTS')"
          :active="activePanel === 'participants'"
          :aria-expanded="activePanel === 'participants'"
          @click="showParticipants"
        />
        <CallControl
          v-if="expanded"
          icon="i-lucide-message-square"
          :label="
            t(chatOpen ? 'INTERNAL_CALL.CLOSE_CHAT' : 'INTERNAL_CALL.OPEN_CHAT')
          "
          :active="activePanel === 'chat'"
          :aria-expanded="activePanel === 'chat'"
          @click="togglePanel('chat', $event)"
        />
        <CallControl
          icon="i-lucide-settings-2"
          :label="t('INTERNAL_CALL.SETTINGS')"
          :active="activePanel === 'settings'"
          :aria-expanded="activePanel === 'settings'"
          @click="togglePanel('settings', $event)"
        />
        <CallControl
          v-if="state.paused"
          icon="i-lucide-play"
          :label="t('INTERNAL_CALL.RETURN')"
          @click="run(() => internalCall.resume())"
        />
        <span
          v-if="expanded"
          class="mx-0.5 h-6 w-px bg-n-weak"
          aria-hidden="true"
        />
        <CallControl
          icon="i-lucide-phone-off"
          :label="t('INTERNAL_CALL.LEAVE')"
          danger
          @click="run(() => internalCall.close())"
        />
      </footer>
      <div ref="media" class="hidden" aria-hidden="true" />
    </section>
  </Teleport>
  <Dialog
    ref="returnDialog"
    :title="t('INTERNAL_CALL.RETURN_TITLE')"
    :description="t('INTERNAL_CALL.RETURN_DESCRIPTION')"
    :confirm-button-label="t('INTERNAL_CALL.RETURN')"
    :cancel-button-label="t('INTERNAL_CALL.STAY_PAUSED')"
    @confirm="run(() => internalCall.resume())"
    @close="state.returnOffer = false"
  />
</template>
